import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "shared/Button";
import styles from "./ThumbnailPreview.module.css";
import classNames from "classnames";

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function getThumbnailSrc(thumbnail) {
  if (!thumbnail) return null;

  if (typeof thumbnail === "string") return thumbnail;
  return thumbnail.src || thumbnail.url || thumbnail.previewUrl || null;
}

function getThumbnailAlt(thumbnail) {
  if (!thumbnail || typeof thumbnail === "string") return "Thumbnail preview";
  return thumbnail.alt || "Thumbnail preview";
}

// Thumbnail image in the media-edit
export function ThumbnailPreview({
  thumbnail,
  onAddExisting,
  onDrop,
  onEdit,
  onDelete,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const thumbnailSrc = getThumbnailSrc(thumbnail);
  const hasThumbnail = Boolean(thumbnailSrc);

  // Hide border if ANY field in thumbnail object has a value (not null, not empty string, not undefined)
  const hasAnyContent =
    thumbnail &&
    (typeof thumbnail === "string" ||
      Object.values(thumbnail).some(
        (val) => val !== null && val !== "" && val !== undefined,
      ));
  const shouldHideBorder = hasAnyContent;

  const openFilePicker = () => {
    onAddExisting();
    inputRef.current?.click();
  };

  const handleInputChange = (event) => {
    const file = event.target.files[0];
    event.target.value = "";

    if (!file || !file.type.startsWith("image/")) {
      return;
    }

    onAddExisting(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];
    if (!file || !file.type.startsWith("image/")) {
      return;
    }

    onDrop(file);
  };

  return (
    <section className={classNames(styles.card)}>
      <div className={styles.inner}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>Thumbnail Image</h2>
          <div className={styles.divider} />
        </div>

        <div className={styles.content}>
          <div
            className={`${styles.dropZone} ${
              isDragging ? styles.dragging : ""
            } ${shouldHideBorder ? styles.noBorder : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="img"
            aria-label={
              hasThumbnail ? "Thumbnail preview" : "No thumbnail image"
            }
          >
            {hasThumbnail ? (
              <img
                src={thumbnailSrc}
                alt={getThumbnailAlt(thumbnail)}
                className={styles.preview}
              />
            ) : (
              <span className={styles.emptyLabel}>No Image</span>
            )}
          </div>

          <div
            className={`${styles.actions} ${
              hasThumbnail ? styles.actionsRow : ""
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={handleInputChange}
            />

            {hasThumbnail ? (
              <button
                className={styles.deleteButton}
                onClick={onDelete}
                aria-label="Delete thumbnail"
                type="button"
              >
                <TrashIcon />
              </button>
            ) : (
              <>
                <Button
                  className={styles.editOrCreateButton}
                  onClick={onEdit}
                  type="button"
                >
                  Create New
                </Button>
                <Button
                  className={styles.addExistingButton}
                  onClick={openFilePicker}
                  type="button"
                >
                  Add Existing
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

ThumbnailPreview.propTypes = {
  thumbnail: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      src: PropTypes.string,
      url: PropTypes.string,
      previewUrl: PropTypes.string,
      previewSrc: PropTypes.string,
      alt: PropTypes.string,
      iconBadge: PropTypes.any,
      badge: PropTypes.any,
    }),
  ]),
  onAddExisting: PropTypes.func,
  onDrop: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

ThumbnailPreview.defaultProps = {
  thumbnail: null,
  onAddExisting: () => {},
  onDrop: () => {},
  onEdit: () => {},
  onDelete: () => {},
};
